import React, { useRef, useState } from 'react';
import { Modal, Pressable, Text, View, useWindowDimensions } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { RADIUS, shadow, ThemePreference } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { logout } from '../redux/slices/authSlice';
import Avatar from '../components/ui/Avatar';
import { roleLabel } from './navItems';

type IconName = React.ComponentProps<typeof MaterialCommunityIcons>['name'];

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: IconName }[] = [
  { value: 'light', label: 'Mode jour', icon: 'white-balance-sunny' },
  { value: 'dark', label: 'Mode nuit', icon: 'weather-night' },
  { value: 'system', label: 'Thème du système', icon: 'theme-light-dark' },
];

type Anchor = { top: number; right: number };

/** Airbnb-style account pill (menu icon + avatar) opening the account dropdown. */
export default function UserMenu() {
  const styles = useStyles();
  const { colors, preference, setPreference } = useAppTheme();
  const { width: windowWidth } = useWindowDimensions();
  const dispatch = useAppDispatch();
  const username = useAppSelector((s) => s.auth.username);
  const role = useAppSelector((s) => s.auth.role);
  const pillRef = useRef<View>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  const open = () => {
    pillRef.current?.measureInWindow((x, y, width, height) => {
      setAnchor({ top: y + height + 8, right: Math.max(8, windowWidth - (x + width)) });
    });
  };
  const close = () => setAnchor(null);

  return (
    <>
      <Pressable
        ref={pillRef}
        onPress={open}
        accessibilityRole="button"
        accessibilityLabel="Menu du compte"
        accessibilityState={{ expanded: !!anchor }}
        style={(state) => {
          const hovered = (state as { hovered?: boolean }).hovered;
          return [styles.pill, hovered || anchor ? shadow(colors, 2) : null];
        }}
      >
        <MaterialCommunityIcons name="menu" size={18} color={colors.textPrimary} />
        <Avatar name={username} size={30} />
      </Pressable>

      <Modal visible={!!anchor} transparent animationType="fade" onRequestClose={close}>
        <Pressable style={styles.backdrop} onPress={close} accessibilityLabel="Fermer le menu" />
        {anchor ? (
          <View style={[styles.menu, shadow(colors, 3), { top: anchor.top, right: anchor.right }]} accessibilityRole="menu">
            <View style={styles.header}>
              <Avatar name={username} size={40} />
              <View style={styles.headerText}>
                <Text style={styles.username} numberOfLines={1}>{username ?? 'Utilisateur'}</Text>
                <Text style={styles.role}>{roleLabel(role)}</Text>
              </View>
            </View>
            <View style={styles.divider} />
            <Text style={styles.sectionLabel}>Apparence</Text>
            {THEME_OPTIONS.map((option) => {
              const active = preference === option.value;
              return (
                <MenuRow
                  key={option.value}
                  icon={option.icon}
                  label={option.label}
                  active={active}
                  onPress={() => { setPreference(option.value); close(); }}
                />
              );
            })}
            <View style={styles.divider} />
            <MenuRow icon="logout" label="Déconnexion" onPress={() => { close(); dispatch(logout()); }} />
          </View>
        ) : null}
      </Modal>
    </>
  );
}

function MenuRow({ icon, label, active, onPress }: { icon: IconName; label: string; active?: boolean; onPress: () => void }) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="menuitem"
      accessibilityState={{ checked: active }}
      style={(state) => {
        const hovered = (state as { hovered?: boolean }).hovered;
        return [styles.row, (hovered || state.pressed) && styles.rowHover];
      }}
    >
      <MaterialCommunityIcons name={icon} size={20} color={colors.textPrimary} />
      <Text style={[styles.rowLabel, active && styles.rowLabelActive]}>{label}</Text>
      {active ? <MaterialCommunityIcons name="check" size={18} color={colors.primary} /> : null}
    </Pressable>
  );
}

const useStyles = makeStyles((c) => ({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingLeft: 14,
    paddingRight: 5,
    paddingVertical: 5,
    borderRadius: RADIUS.pill,
    borderWidth: 1,
    borderColor: c.border,
    backgroundColor: c.surface,
  },
  backdrop: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 },
  menu: {
    position: 'absolute',
    width: 280,
    backgroundColor: c.surfaceRaised,
    borderRadius: RADIUS.lg,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: c.divider,
  },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 10 },
  headerText: { flexShrink: 1 },
  username: { fontSize: 15, fontWeight: '700', color: c.textPrimary },
  role: { fontSize: 13, color: c.textSecondary, marginTop: 2 },
  divider: { height: 1, backgroundColor: c.divider, marginVertical: 6 },
  sectionLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: c.textMuted,
    paddingHorizontal: 16,
    paddingTop: 6,
    paddingBottom: 4,
  },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, minHeight: 44 },
  rowHover: { backgroundColor: c.surfaceAlt },
  rowLabel: { flex: 1, fontSize: 14, color: c.textPrimary },
  rowLabelActive: { fontWeight: '700' },
}));
